<template>
  <div
    v-editable
    class="relative h-[360px] w-full bg-cover bg-center bg-no-repeat"
    :class="[
      marginClasses,
      {
        'mx-break-out':
          (!cmsParentBlok || cmsParentBlok.component === 'BlogOverview') &&
          blok.wholeWidth,
      },
      { 'cursor-pointer': ctaButton },
    ]"
  >
    <AssetImage :blok="backGroundImage" cover />
    <CMSLink
      v-if="ctaButton"
      :cms-link="ctaButton?.ctaLink"
      :open-new-tab="ctaButton?.openNewTab"
      :product-id="ctaButton?.product_id"
      :order-number="ctaButton?.order_number"
      @click="handleClick(ctaButton)"
    >
      <div
        class="absolute top-0 flex size-full flex-col items-center justify-center px-10 py-12 md:p-11"
        :class="{
          'md:items-center': blok.textLayout === 'textCentered',
          'md:items-start': blok.textLayout === 'textLeft',
          'md:items-end': blok.textLayout === 'textRight',
          'text-black': blok.fontColor === 'black',
          'text-white': blok.fontColor === 'white',
        }"
      >
        <div class="max-w-2xl text-center">
          <h2 class="mb-[15px] text-[24px] font-bold md:text-[60px]">
            {{ blok.headline }}
          </h2>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="content" />
          <template v-if="!blok.hide_button">
            <Intersect
              v-for="button in blok.CtaButton"
              :key="button._uid"
              :threshold="0.5"
              @enter.once="handleVisibility(button)"
            >
              <component :is="button.component" :blok="button" />
            </Intersect>
          </template>
        </div>
      </div>
    </CMSLink>
    <div
      v-else
      class="absolute top-0 flex size-full flex-col items-center justify-center px-10 py-12 md:p-11"
      :class="{
        'md:items-center': blok.textLayout === 'textCentered',
        'md:items-start': blok.textLayout === 'textLeft',
        'md:items-end': blok.textLayout === 'textRight',
        'text-black': blok.fontColor === 'black',
        'text-white': blok.fontColor === 'white',
      }"
    >
      <div class="max-w-2xl text-center">
        <h2 class="mb-[15px] text-[24px] font-bold md:text-[60px]">
          {{ blok.headline }}
        </h2>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="content" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CTA_PROMOTION_CONTEXT_KEY } from '#layers/base/constants/injectionKeys'
import type {
  SbBannerValantic,
  SbCtaButton,
} from '~/cms/storyblok/types/storyblok.d.ts'

const props = defineProps({
  blok: {
    type: Object as PropType<SbBannerValantic>,
    default: () => {},
  },
  category: {
    type: String as PropType<string>,
    default: undefined,
  },
})

const { trackPromotionSelect, trackPromotionView } = useTrackingEvents()
const { marginClasses } = useStoryblokMargins(props.blok)
const { content } = useRichtext(props.blok.textArea)

// This block emits the promotion events for the CTA it nests, using its own
// parameters, so the CTA must not emit competing ones (ECOM-2766).
provide(CTA_PROMOTION_CONTEXT_KEY, { claimedBy: 'bannervalantic' })

const backGroundImage = computed(() => props.blok.ImageDesktop[0])

const instance = getCurrentInstance()
const cmsParentBlok = computed(
  () =>
    ((instance as any)?.parent?.props?.blok as { component?: string }) || null,
)

const ctaButton = computed(() => props.blok.CtaButton?.[0])

const handleClick = (_button?: SbCtaButton) => {
  trackPromotionSelect({
    id: props.blok.promotion_id || 'banner_valantic_promotion_id',
    name: props.blok.promotion_name || 'banner_valantic_promotion_name',
    promoType: 'bannervalantic',
    position: 1,
    creativeNameSb: props.blok.creative_name || '',
  })
}

const handleVisibility = (_button?: SbCtaButton) => {
  trackPromotionView({
    id: props.blok.promotion_id || 'banner_valantic_promotion_id',
    name: props.blok.promotion_name || 'banner_valantic_promotion_name',
    promoType: 'bannervalantic',
    position: 1,
    creativeNameSb: props.blok.creative_name || '',
  })
}
</script>
